import { useCallback } from "react";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { analyticsEvents, eventTypes } from "lib/utils/constants";
import { publishPostMessage } from "lib/utils/helpers";
import {
  handleWishlistAddItem,
  handleWishlistGetItems,
  handleWishlistRemoveItem,
} from "lib/utils/wishlist-v2";
import useSendAnalyticsEvent from "./useAnalytics";
import { useLocale } from "./useLocale";

export const useWishlistSDK = (merchantId: string, customerId: string) => {
  const { t } = useLocale();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const {
    state: { isLoggedInOnStore },
  } = useAuthContext();

  /**
   * The merchant's Shopflo.addToWishlist() and friends await a response event
   * before resolving, so a blocked call has to answer rather than fall silent -
   * dropping it would hang their promise until the SDK's timeout fires.
   *
   * isLoggedInOnStore is undefined until the storefront script answers, and
   * that counts as blocked here, matching every other wishlist gate in the app.
   */
  const isStoreLoggedOut = !Boolean(isLoggedInOnStore);

  // Resolved at call time, not at mount: t() hands back the raw key until i18n
  // has its resources, and a reject built on the first render would keep that
  // placeholder forever.
  const rejectAsStoreLoggedOut = useCallback(
    (responseEventType: string) => {
      publishPostMessage(responseEventType, {
        success: false,
        error: { message: t("store_logged_out_error") },
        context: "SDK",
      });
    },
    [t],
  );

  const addItem = useCallback(
    async (payload: { productId?: string; variantId?: string }) => {
      if (isStoreLoggedOut) {
        rejectAsStoreLoggedOut(eventTypes.WISHLIST_ITEM_ADDED);
        return;
      }
      const success = await handleWishlistAddItem(merchantId, customerId, payload, "SDK");
      if (success) {
        sendAnalyticsEvent({
          eventName: analyticsEvents.ADDED_TO_WISHLIST,
          eventType: "flo_action",
          metaData: {
            wishlistData: {
              productId: payload?.productId,
              variantId: payload?.variantId,
            },
          },
        });
      }
    },
    [merchantId, customerId, sendAnalyticsEvent, isStoreLoggedOut, rejectAsStoreLoggedOut],
  );

  const removeItem = useCallback(
    async (payload: { productId?: string; variantId?: string }) => {
      if (isStoreLoggedOut) {
        rejectAsStoreLoggedOut(eventTypes.WISHLIST_ITEM_REMOVED);
        return;
      }
      const success = await handleWishlistRemoveItem(merchantId, customerId, payload, "SDK");
      if (success) {
        sendAnalyticsEvent({
          eventName: analyticsEvents.REMOVED_FROM_WISHLIST,
          eventType: "flo_action",
          metaData: {
            wishlistData: {
              productId: payload?.productId,
              variantId: payload?.variantId,
            },
          },
        });
      }
    },
    [merchantId, customerId, sendAnalyticsEvent, isStoreLoggedOut, rejectAsStoreLoggedOut],
  );

  const getItems = useCallback(
    (payload: { limit?: number; offset?: number }) => {
      if (isStoreLoggedOut) {
        rejectAsStoreLoggedOut(eventTypes.WISHLIST_ITEMS_DATA);
        return;
      }
      return handleWishlistGetItems(merchantId, customerId, payload);
    },
    [merchantId, customerId, isStoreLoggedOut, rejectAsStoreLoggedOut],
  );

  return { addItem, removeItem, getItems };
};
